import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { PersistentTabStore, TabWithID } from '@app/core/state/persistent-tab/persistent-tab.store';
import { Tab, TabConfig } from '@app/shared/tab-bar/tab-bar.component';
import { deleteEntities, getActiveEntity, getActiveId, getAllEntities, getEntity, moveEntity, selectActiveEntity, selectActiveId, selectAllEntities, setActiveId, updateEntities, upsertEntities } from '@ngneat/elf-entities';
import { KeyValue, Payload } from '@pezi/entities';
import { Observable, of } from 'rxjs';
import { CompareUtil } from '../../util/compare.util';

@Injectable({providedIn: 'root'})
export class PersistentTabService {

  private previousActiveTab: Tab;

  constructor(private persistentTabStore: PersistentTabStore,
              private router: Router) {
  }

  clearPersistentTabStore() {
    this.persistentTabStore.data.reset();
  }

  getTab(id: string): Tab {
    return this.persistentTabStore.data.query(getEntity(id));
  }

  getTabIDForURL(url: string): string {
    const tabs = this.getTabs();
    if (tabs && url) {
      return tabs.find(t => t.config.idURL && url.startsWith(t.config.idURL)) ? tabs.find(t => url.startsWith(t.config.idURL)).config.idURL : null;
    }
    return null;
  }

  getTabs(): TabWithID[] {
    return this.persistentTabStore.data.query(getAllEntities());
  }

  getTabs$(): Observable<Tab[]> {
    return this.persistentTabStore.data.pipe(selectAllEntities());
  }

  getCachedDataFromTabs<T>(idURL: string, version?: number): Observable<T & Payload> {
    const cachedTab = this.getTab(idURL);
    if (cachedTab?.data && (!version || CompareUtil.hasSameVersion(version, cachedTab.data))) {
      return of({
        data:    cachedTab.data,
        success: true
      } as Payload & any);
    }
    return null;
  }

  getCachedDataFromTabsBetreuerVertreter<T>(idURL: string, version?: number): Observable<T & Payload> {
    const cachedTab = this.getTab(idURL);
    if (cachedTab?.data && (!version || CompareUtil.hasSameBetreuerVertreterVersion(version, cachedTab.data))) {
      return of({
        data:    cachedTab.data,
        success: true
      } as Payload & any);
    }
    return null;
  }

  /**
   * Used for cached Personstate since reloading for diverging versions is needed
   * @param idURL
   * @param versionComparison key: property inside a State containg a "version", value: version
   */
  getCachedStateFromTabsVersionComparison<T>(idURL: string, versionComparison: KeyValue<string, number>): T {
    const cachedTab = this.getTab(idURL);
    if (cachedTab?.data && (!versionComparison || !versionComparison.value ||
      (cachedTab.data[versionComparison.key] && CompareUtil.hasSameVersion(versionComparison.value, cachedTab.data[versionComparison.key])))) {
      return cachedTab.data;
    }
    return null;
  }

  /**
   * Used for cached Polizzenstate since it has a lot of different properties that can be loaded individually
   * @param idURL
   * @param entity
   */
  getCachedStateFromTabs<T>(idURL: string, entity: string): Observable<T & Payload> {
    const cachedTab = this.getTab(idURL);
    if (cachedTab?.data && cachedTab.data[entity]) {
      return of({
        data:    cachedTab.data[entity],
        success: true
      } as Payload & any);
    }
    return null;
  }

  /**
   * Adds a new tab. Initial data can be optionally added through "tab.data".
   * @param tab
   * @param setActive
   * @param updateUrl
   */
  addTab(tab: Tab, setActive = true, updateUrl?: string): void {
    const currentTabs = this.getTabs();
    let newTabIndex = 0;

    let storeTab: Partial<TabWithID> = {};
    storeTab = Object.assign(tab, storeTab);
    storeTab.id = tab.config.idURL;

    if (currentTabs) {
      newTabIndex = currentTabs.findIndex(v => v.config.idURL === tab.config.idURL);
      if (newTabIndex !== -1) {
        // Get current url from saved tabs
        storeTab.config.url = currentTabs[newTabIndex].config.url;
      }
    }
    // Manual override
    if (updateUrl) {
      storeTab.config.url = updateUrl;
    }
    if (!storeTab.config.url) {
      // Fallback
      storeTab.config.url = this.router.url && this.router.url.startsWith(storeTab.config.idURL) ? this.router.url : storeTab.config.fallbackURL;
    }
    this.persistentTabStore.data.update(upsertEntities(storeTab));

    if (newTabIndex === -1 && this.previousActiveTab) {
      const previousIndex = currentTabs.findIndex(v => v.config.idURL === this.previousActiveTab.config.idURL);
      if (previousIndex !== -1 && newTabIndex !== -1) { // den aktuelle eingefügten tab, rechts neben den davor aktiven tab schieben
        this.persistentTabStore.data.update(moveEntity({
          fromIndex: newTabIndex,
          toIndex:   previousIndex + 1
        }));
      }
    }
    if (setActive) {
      this.setActive(tab.config.idURL);
    }
  }

  /**
   * Use this if you want to update stored data within a tab and not overwrite existing properties outside of parameter selectiveUpdateProperties.
   * Otherwise just use addtab.
   * @param tab =config. If no stored data present, this tab will be added instead as fallback
   * @param selectiveUpdateProperties
   */
  updateTabStateProperties(tab: Tab, selectiveUpdateProperties?: KeyValue<string, any>[]): void {
    const currentTabs = this.getTabs();
    const tabIdx = currentTabs.findIndex(v => v.config.idURL === tab.config.idURL);
    const isNewTabInList = currentTabs && tabIdx === -1;
    if (!selectiveUpdateProperties || selectiveUpdateProperties.length === 0 || isNewTabInList || !currentTabs[tabIdx].id || !currentTabs[tabIdx].data) {
      this.addTab(tab);
    } else {
      selectiveUpdateProperties.forEach(prop => {
        currentTabs[tabIdx].data[prop.key] = prop.value;
      });
      this.persistentTabStore.data.update(updateEntities(currentTabs[tabIdx].id,
        (entity) =>
          (
            {
              ...entity,
              // Update config Labels (needed on Version change)
              config: {
                        idURL:       entity.config.idURL,
                        url:         entity.config.url,
                        icon:        tab.config.icon,
                        label:       tab.config.label,
                        sublabel:    tab.config.sublabel,
                        fallbackURL: entity.config.fallbackURL
                      } as TabConfig,
              data: currentTabs[tabIdx].data
            }
          )
      ));
    }
  }

  removeTab(tab: Tab): void {
    this.persistentTabStore.data.update(deleteEntities(tab.config.idURL));
  }

  moveTab(fromIdx: number, toIdx: number): void {
    this.persistentTabStore.data.update(moveEntity({
      fromIndex: fromIdx,
      toIndex:   toIdx
    }));
  }

  isActive(tab: Tab): boolean {
    const id = this.persistentTabStore.data.query(getActiveId);
    return id && tab.config.idURL === id;
  }

  setActive(id: string): void {
    this.previousActiveTab = this.persistentTabStore.data.query(getActiveEntity());
    this.persistentTabStore.data.update(setActiveId(id));
  }

  getActiveTab(): Tab {
    return this.persistentTabStore.data.query(getActiveEntity());
  }

  getActiveTab$(): Observable<Tab> {
    return this.persistentTabStore.data.pipe(selectActiveEntity());
  }

  getActiveId$(): Observable<string> {
    return this.persistentTabStore.data.pipe(selectActiveId());
  }
}

